import { deduplicateById, validateMediaItems } from '../utils/deduplication';
import { API_BASE_URL, MDBLIST_COLLECTIONS } from '../config/app-config';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.pendingRequests = new Map();
    this.requestCache = new Map();
    this.cacheTimeout = 30000;
    this.batchRequests = new Map();
    this.batchTimeout = 100;
    this.priorityQueue = new Map();
    this.correlationId = null; // Track correlation ID for current request chain
  }

  getCacheKey(endpoint, options = {}) {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    // Include the full endpoint with query params for proper cache isolation
    return `${method}:${endpoint}:${body}`;
  }

  isCacheValid(cached) {
    return cached && (Date.now() - cached.timestamp) < this.cacheTimeout;
  }

  getOptimizedHeaders(method, customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders
    };

    if (method === 'GET') {
      headers['Accept-Encoding'] = 'gzip, deflate, br';
    }
    
    // Add correlation ID if available
    if (this.correlationId) {
      headers['X-Correlation-Id'] = this.correlationId;
    }

    return headers;
  }

  cleanupCache() {
    const now = Date.now();
    for (const [key, cached] of this.requestCache.entries()) {
      if (now - cached.timestamp > this.cacheTimeout) {
        this.requestCache.delete(key);
      }
    }
  }


  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';
    const cacheKey = this.getCacheKey(endpoint, options);
    
    if (method === 'GET') {
      const cached = this.requestCache.get(cacheKey);
      if (cached && this.isCacheValid(cached)) {
        return cached.data;
      }
    }

    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const config = {
      headers: this.getOptimizedHeaders(method, options.headers),
      ...options,
    };

    const requestPromise = this.makeRequest(url, config, cacheKey, method);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  async makeRequest(url, config, cacheKey, method) {
    try {
      const response = await fetch(url, config);
      
      // Extract correlation ID from response headers
      const correlationId = response.headers.get('X-Correlation-Id');
      if (correlationId && !this.correlationId) {
        this.correlationId = correlationId;
      }
      
      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const delay = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
          
          console.warn(`Rate limited. Retrying after ${delay}ms`);
          
          await new Promise(resolve => setTimeout(resolve, Math.min(delay, 10000)));
          return this.makeRequest(url, config, cacheKey, method);
        }

        const errorData = await response.json().catch(() => ({
          success: false,
          error: { message: 'Unknown error occurred' }
        }));
        
        const msg = errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`;
        if (response.status >= 500) {
          const cid = response.headers.get('X-Correlation-Id');
          console.error(
            `API ${response.status} ${url}`,
            cid ? `correlation=${cid}` : '',
            msg
          );
        }
        throw new Error(msg);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error?.message || 'API request failed');
      }

      const result = data.data;
      if (
        method === 'GET' &&
        result &&
        typeof result === 'object' &&
        !Array.isArray(result) &&
        typeof result.hasMore === 'undefined'
      ) {
        const hm = response.headers.get('X-Has-More');
        if (hm !== null) {
          result.hasMore = hm === 'true' || hm === '1';
        }
      }
      if (data.metadata) {
        result._metadata = data.metadata;
      }
      // Preserve list_info for collection endpoints
      if (data.list_info) {
        result.list_info = data.list_info;
      }

      if (method === 'GET') {
        this.requestCache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });

        if (this.requestCache.size > 100) {
          this.cleanupCache();
        }
      }

      return result;
    } catch (error) {
      throw error;
    }
  }

  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    const searchParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    return searchParams.toString();
  }

  async batchPageRequests(endpoint, pages, baseParams = {}) {
    const requests = pages.map(page => {
      const params = { ...baseParams, page };
      const queryString = this.buildQueryString(params);
      return this.request(`${endpoint}?${queryString}`);
    });

    return Promise.all(requests);
  }

  async searchMulti(query, page = 1) {
    const queryString = this.buildQueryString({ query, page });
    const response = await this.request(`/search/multi?${queryString}`);
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async searchMovies(query, page = 1, options = {}) {
    const params = { query, page, ...options };
    const queryString = this.buildQueryString(params);
    const response = await this.request(`/search/movies?${queryString}`);
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async searchTV(query, page = 1, options = {}) {
    const params = { query, page, ...options };
    const queryString = this.buildQueryString(params);
    const response = await this.request(`/search/tv?${queryString}`);
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async getMovies(params = {}, pages = null) {
    if (pages && Array.isArray(pages) && pages.length > 1) {
      const results = await this.batchPageRequests('/movies', pages, params);
      
      const allResults = results.flatMap(result => result.results || []);
      const validatedResults = validateMediaItems(allResults);
      const deduplicatedResults = deduplicateById(validatedResults);
      
      return {
        results: deduplicatedResults,
        total_results: results[0]?.total_results || 0,
        total_pages: results[0]?.total_pages || 0,
        page: Math.min(...pages)
      };
    } else {
      const queryString = this.buildQueryString(params);
      const response = await this.request(`/movies?${queryString}`);
      
      if (response.results) {
        response.results = validateMediaItems(response.results);
      }
      
      return response;
    }
  }

  async getMediaBatch(movieIds = [], tvIds = []) {
    return this.request('/media/batch', {
      method: 'POST',
      body: JSON.stringify({ movieIds, tvIds }),
    });
  }

  async getMovieDetails(movieId, language = null) {
    if (!language) return this.request(`/movies/${movieId}`);
    // Some backends expect `lang` instead of `language` — send both.
    const qs = this.buildQueryString({ language, lang: language });
    return this.request(`/movies/${movieId}?${qs}`);
  }

  async getMovieDetailsComplete(movieId, language = null) {
    const lang = language || localStorage.getItem('language') || 'lt';
    const queryString = this.buildQueryString({ language: lang });
    return this.request(`/movies/${movieId}/complete?${queryString}`);
  }

  async getMovieCredits(movieId) {
    return this.request(`/movies/${movieId}/credits`);
  }

  async getMovieVideos(movieId) {
    return this.request(`/movies/${movieId}/videos`);
  }

  async getSimilarMovies(movieId, page = 1) {
    const queryString = this.buildQueryString({ page });
    return this.request(`/movies/${movieId}/similar?${queryString}`);
  }

  async getMovieWatchProviders(movieId) {
    return this.request(`/movies/${movieId}/watch/providers`);
  }

  async getListById(listId, params = {}) {
    // Add language parameter from localStorage if not specified
    const language = params.language || localStorage.getItem('language') || 'lt';
    const paramsWithLang = { ...params, language };
    const queryString = this.buildQueryString(paramsWithLang);
    const response = await this.request(`/movies/list-by-id/${listId}?${queryString}`);
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async getMoviesWithRatingFilters(params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 20
    };

    // Convert IMDB rating from 0-10 to 0-100 scale for MDBList
    if (params.imdb_rating > 0) {
      queryParams.filter_imdbrating_min = Math.round(params.imdb_rating * 10);
    }

    // RT rating is already 0-100
    if (params.rt_rating > 0) {
      queryParams.filter_tomatorating_min = Math.round(params.rt_rating);
    }

    if (params.with_genres && params.with_genres !== 'all') {
      queryParams.filter_genre = params.with_genres;
    }

    // Pass Lithuania availability filter parameters
    if (params.watch_region) {
      queryParams.watch_region = params.watch_region;
    }
    if (params.with_watch_monetization_types) {
      queryParams.with_watch_monetization_types = params.with_watch_monetization_types;
    }
    if (params.with_watch_providers) {
      queryParams.with_watch_providers = params.with_watch_providers;
    }

    // Pass date range filters directly - backend will convert to MDBList format
    if (params['primary_release_date.gte']) {
      queryParams['primary_release_date.gte'] = params['primary_release_date.gte'];
    }
    if (params['primary_release_date.lte']) {
      queryParams['primary_release_date.lte'] = params['primary_release_date.lte'];
    }

    const queryString = this.buildQueryString(queryParams);
    const listPath = MDBLIST_COLLECTIONS.MOVIES;
    const response = await this.request(`/movies/list/${listPath}?${queryString}`);
    
    if (response?.data?.results) {
      response.data.results = validateMediaItems(response.data.results);
      return response.data;
    }
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async getTVShowsWithRatingFilters(params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 20
    };

    // Convert IMDB rating from 0-10 to 0-100 scale for MDBList
    if (params.imdb_rating > 0) {
      queryParams.filter_imdbrating_min = Math.round(params.imdb_rating * 10);
    }

    // RT rating is already 0-100
    if (params.rt_rating > 0) {
      queryParams.filter_tomatorating_min = Math.round(params.rt_rating);
    }

    if (params.with_genres && params.with_genres !== 'all') {
      queryParams.filter_genre = params.with_genres;
    }

    // Pass Lithuania availability filter parameters
    if (params.watch_region) {
      queryParams.watch_region = params.watch_region;
    }
    if (params.with_watch_monetization_types) {
      queryParams.with_watch_monetization_types = params.with_watch_monetization_types;
    }
    if (params.with_watch_providers) {
      queryParams.with_watch_providers = params.with_watch_providers;
    }

    // Pass date range filters directly - backend will convert to MDBList format
    if (params['first_air_date.gte']) {
      queryParams['first_air_date.gte'] = params['first_air_date.gte'];
    }
    if (params['first_air_date.lte']) {
      queryParams['first_air_date.lte'] = params['first_air_date.lte'];
    }

    const queryString = this.buildQueryString(queryParams);
    const listPath = MDBLIST_COLLECTIONS.TV_SHOWS;
    const response = await this.request(`/movies/list/${listPath}?${queryString}`);
    
    if (response?.data?.results) {
      response.data.results = validateMediaItems(response.data.results);
      return response.data;
    }
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  // REMOVED: getBulkMovieRatings() method - no longer needed because
  // all movie endpoints already include enhanced_ratings in responses

  async getTVShows(params = {}, pages = null) {
    if (pages && Array.isArray(pages) && pages.length > 1) {
      const results = await this.batchPageRequests('/tv', pages, params);
      
      const allResults = results.flatMap(result => result.results || []);
      const validatedResults = validateMediaItems(allResults);
      const deduplicatedResults = deduplicateById(validatedResults);
      
      return {
        results: deduplicatedResults,
        total_results: results[0]?.total_results || 0,
        total_pages: results[0]?.total_pages || 0,
        page: Math.min(...pages)
      };
    } else {
      const queryString = this.buildQueryString(params);
      const response = await this.request(`/tv?${queryString}`);
      
      if (response.results) {
        response.results = validateMediaItems(response.results);
      }
      
      return response;
    }
  }

  async getTVDetails(tvId, language = null) {
    if (!language) return this.request(`/tv/${tvId}`);
    const qs = this.buildQueryString({ language, lang: language });
    return this.request(`/tv/${tvId}?${qs}`);
  }

  async getTVDetailsComplete(tvId, language = null) {
    const lang = language || localStorage.getItem('language') || 'lt';
    const queryString = this.buildQueryString({ language: lang });
    return this.request(`/tv/${tvId}/complete?${queryString}`);
  }

  async getTVCredits(tvId) {
    return this.request(`/tv/${tvId}/credits`);
  }

  async getTVVideos(tvId) {
    return this.request(`/tv/${tvId}/videos`);
  }

  async getSimilarTVShows(tvId, page = 1) {
    const queryString = this.buildQueryString({ page });
    return this.request(`/tv/${tvId}/similar?${queryString}`);
  }

  async getTVWatchProviders(tvId) {
    return this.request(`/tv/${tvId}/watch/providers`);
  }

  async getTVSeason(tvId, seasonNumber) {
    return this.request(`/tv/${tvId}/season/${seasonNumber}`);
  }

  async getTrendingMovies(timeWindow = 'day', page = 1) {
    const queryString = this.buildQueryString({ time_window: timeWindow, page });
    const response = await this.request(`/movies/trending?${queryString}`);
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async getTrendingTV(timeWindow = 'day', page = 1) {
    const queryString = this.buildQueryString({ time_window: timeWindow, page });
    const response = await this.request(`/tv/trending?${queryString}`);
    
    if (response.results) {
      response.results = validateMediaItems(response.results);
    }
    
    return response;
  }

  async getCollections(params = {}) {
    const queryParams = {
      limit: params.limit || 100,
      offset: params.offset || 0
    };
    
    if (params.type && params.type !== 'all') {
      queryParams.type = params.type;
    }
    
    if (params.search) {
      queryParams.search = params.search;
    }

    const queryString = this.buildQueryString(queryParams);
    return this.request(`/movies/collections?${queryString}`);
  }

  /** Aggregate collection counts (total / movie / TV) from the API — single cheap request, server-cached. */
  async getCollectionsStats() {
    return this.request('/movies/collections/stats');
  }

  getStats() {
    return {
      cacheSize: this.requestCache.size,
      pendingRequests: this.pendingRequests.size,
      cacheTimeout: this.cacheTimeout
    };
  }

  clearCache() {
    this.requestCache.clear();
    this.pendingRequests.clear();
  }

  extractYouTubeId(url) {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  }

  async getSearchSuggestions(query) {
    if (!query || query.trim().length < 2) return { results: [] };
    const response = await this.searchMulti(query, 1);
    return { results: (response.results || []).slice(0, 5) };
  }

  async getComments(movieId, mediaType = 'movie', page = 1, limit = 20) {
    const queryString = this.buildQueryString({ page, limit });
    return this.request(`/comments/${mediaType}/${movieId}?${queryString}`);
  }

  // Cinema / Forum Cinemas endpoints
  async getCinemaLocations() {
    return this.request('/cinema/locations');
  }

  async getCinemaShowtimes(cinema = 'vilnius', date = null) {
    const params = { cinema };
    if (date) params.date = date;
    const queryString = this.buildQueryString(params);
    return this.request(`/cinema/showtimes?${queryString}`);
  }

  async searchCinema(title, date = null) {
    const params = { title };
    if (date) params.date = date;
    const queryString = this.buildQueryString(params);
    return this.request(`/cinema/search?${queryString}`);
  }
}

const apiService = new ApiService();
export default apiService; 